import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, UserX, ChevronDown, X, Star } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Smart operator selector for production forms.
 *
 * Props:
 *   value        – current operator name (string)
 *   employeeId   – current employee_id (string|null)
 *   machineId    – selected machine id (for smart ordering)
 *   onChange      – ({ employee_id, operator_name, unlisted }) => void
 */
export const OperatorSelect = ({ value = '', employeeId = null, machineId = '', stageMachineIds = [], onChange }) => {
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [unlisted, setUnlisted] = useState(false);
  const [freeText, setFreeText] = useState('');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await axios.get(`${API}/employees/simple?status=active`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEmployees(res.data || []);
      } catch {}
    };
    fetchEmployees();
  }, [token]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Smart ordering: employees assigned to this machine first, then stage machines
  const sorted = useMemo(() => {
    if (!employees.length) return employees;
    // If a specific machine is selected, prioritize those operators
    if (machineId) {
      const assigned = [];
      const rest = [];
      for (const e of employees) {
        if ((e.primary_machines || []).includes(machineId)) assigned.push(e);
        else rest.push(e);
      }
      return [...assigned, ...rest];
    }
    // If no machine but we have stage machine ids, prioritize operators for any machine in this stage
    if (stageMachineIds.length) {
      const stageSet = new Set(stageMachineIds);
      const assigned = [];
      const rest = [];
      for (const e of employees) {
        if ((e.primary_machines || []).some(m => stageSet.has(m))) assigned.push(e);
        else rest.push(e);
      }
      return [...assigned, ...rest];
    }
    return employees;
  }, [employees, machineId, stageMachineIds]);

  const filtered = useMemo(() => {
    if (!search) return sorted;
    const s = search.toLowerCase();
    return sorted.filter(e =>
      e.name.toLowerCase().includes(s) ||
      (e.employee_code || '').toLowerCase().includes(s)
    );
  }, [sorted, search]);

  const isAssigned = (emp) => {
    if (machineId) return (emp.primary_machines || []).includes(machineId);
    if (stageMachineIds.length) return (emp.primary_machines || []).some(m => stageMachineIds.includes(m));
    return false;
  };

  const selectEmployee = (emp) => {
    onChange({ employee_id: emp.id, operator_name: emp.name, unlisted: false });
    setSearch('');
    setOpen(false);
    setUnlisted(false);
    // Blur to dismiss keyboard & reset iOS zoom
    if (inputRef.current) inputRef.current.blur();
    document.activeElement?.blur();
  };

  const handleUnlisted = () => {
    setUnlisted(true);
    setOpen(false);
    setFreeText('');
  };

  const confirmFreeText = () => {
    if (freeText.trim()) {
      onChange({ employee_id: null, operator_name: freeText.trim(), unlisted: true });
    }
  };

  const clearSelection = () => {
    onChange({ employee_id: null, operator_name: '', unlisted: false });
    setUnlisted(false);
    setFreeText('');
  };

  // If in unlisted mode, show free text input
  if (unlisted) {
    return (
      <div className="space-y-1.5" data-testid="operator-select-unlisted">
        <div className="flex items-center gap-1.5">
          <div className="relative flex-1">
            <Input
              className="h-8 text-xs pr-8"
              placeholder="Type operator name..."
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              onBlur={confirmFreeText}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmFreeText(); } }}
              autoFocus
              data-testid="operator-freetext-input"
            />
          </div>
          <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => { setUnlisted(false); clearSelection(); }}>
            Back
          </Button>
        </div>
        <p className="text-[10px] text-amber-600">This entry will be flagged for HR to review</p>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative" data-testid="operator-select">
      {/* Display selected value or trigger */}
      {value ? (
        <div className="flex items-center gap-1.5 h-8 px-3 border rounded-md bg-background text-xs">
          <span className="flex-1 truncate">
            {value}
            {employeeId && <span className="text-[10px] text-slate-400 ml-1">(verified)</span>}
            {!employeeId && value && <span className="text-[10px] text-amber-500 ml-1">(unlisted)</span>}
          </span>
          <button type="button" onClick={clearSelection} className="text-slate-400 hover:text-slate-600">
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="flex items-center gap-2 w-full h-8 px-3 border rounded-md bg-background text-xs text-slate-500 hover:border-slate-400 transition-colors"
          onClick={() => { setOpen(!open); setTimeout(() => inputRef.current?.focus(), 50); }}
          data-testid="operator-select-trigger"
        >
          <Search className="h-3 w-3" />
          <span>Select operator...</span>
          <ChevronDown className="h-3 w-3 ml-auto" />
        </button>
      )}

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-[260px] flex flex-col" data-testid="operator-dropdown">
          <div className="p-1.5 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1.5 h-3 w-3 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                className="w-full h-8 pl-7 pr-2 border rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                style={{ fontSize: '16px' }}
                placeholder="Search name or code..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-3 text-center text-xs text-slate-400">No employees found</div>
            ) : (
              filtered.map(emp => (
                <button
                  key={emp.id}
                  type="button"
                  className="w-full text-left px-3 py-1.5 hover:bg-blue-50 transition-colors flex items-center gap-2"
                  onClick={() => selectEmployee(emp)}
                >
                  {isAssigned(emp) && <Star className="h-3 w-3 text-amber-400 fill-amber-400 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <span className="text-xs font-medium">{emp.name}</span>
                    <span className="text-[10px] text-slate-400 ml-1.5">{emp.employee_code || ''} {emp.designation ? `| ${emp.designation}` : ''}</span>
                  </div>
                </button>
              ))
            )}
          </div>
          <button
            type="button"
            className="w-full text-left px-3 py-2 border-t text-xs text-amber-600 hover:bg-amber-50 flex items-center gap-2 font-medium"
            onClick={handleUnlisted}
            data-testid="operator-not-listed"
          >
            <UserX className="h-3.5 w-3.5" />
            Employee not listed
          </button>
        </div>
      )}
    </div>
  );
};
